import React, { useState, useEffect } from 'react';
import { DollarSign, Scale, Shirt, Percent } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/components/ui/use-toast';
import { getClientAssignedGarments, assignGarmentsToClient } from '@/pages/parametrizacion/clientGarments/Services/clientGarments.services';
import { updateClient } from '@/pages/parametrizacion/clients/Services/clients.services';

const GarmentPricesModal = ({ isOpen, onClose, client, onSuccess }) => {
  const [garments, setGarments] = useState([]);
  const [costoKilo, setCostoKilo] = useState('');
  const [porcentajeExtra, setPorcentajeExtra] = useState('');
  const [porcentajeDominical, setPorcentajeDominical] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const { toast } = useToast();

  const esPorKilo = client?.tipoCobro === 'kilos';

  useEffect(() => {
    if (isOpen && client) {
      setCostoKilo(client.costo_kilo ?? '');
      setPorcentajeExtra(client.porcentajeExtra ?? '');
      setPorcentajeDominical(client.porcentajeDominical ?? '');
      if (!esPorKilo) loadGarments();
    }
  }, [isOpen, client]);

  const loadGarments = async () => {
    setIsLoading(true);
    try {
      const response = await getClientAssignedGarments(client._id);
      setGarments((response.data || []).map(g => ({ ...g, precio: g.precio ?? '' })));
    } catch {
      toast({ title: 'Error', description: 'No se pudieron cargar las prendas del cliente', variant: 'destructive' });
      setGarments([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePriceChange = (prendaId, value) => {
    setGarments(prev => prev.map(g => g.prendaId === prendaId ? { ...g, precio: value } : g));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const clientUpdate = {
        porcentajeExtra: Number(porcentajeExtra) || 0,
        porcentajeDominical: Number(porcentajeDominical) || 0,
      };

      if (esPorKilo) {
        clientUpdate.costo_kilo = Number(costoKilo) || 0;
        await updateClient(client._id, clientUpdate);
      } else {
        await Promise.all([
          updateClient(client._id, clientUpdate),
          assignGarmentsToClient(client._id, garments),
        ]);
      }

      toast({ title: 'Éxito', description: 'Precios guardados correctamente' });
      onSuccess?.();
      onClose();
    } catch {
      toast({ title: 'Error', description: 'No se pudieron guardar los precios', variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-green-600" />
            Configuración de Precios
          </DialogTitle>
          <DialogDescription className="flex items-center gap-2">
            <span>Cliente: <strong>{client?.nombre}</strong></span>
            <Badge variant={esPorKilo ? 'secondary' : 'outline'} className="ml-2 text-xs">
              {esPorKilo ? <><Scale className="h-3 w-3 mr-1 inline" /> Por Kilo</> : <><Shirt className="h-3 w-3 mr-1 inline" /> Por Prenda</>}
            </Badge>
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-4 space-y-5">

          {/* === PRECIO BASE === */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                {esPorKilo ? <Scale className="h-4 w-4 text-blue-600" /> : <Shirt className="h-4 w-4 text-blue-600" />}
                {esPorKilo ? 'Precio por Kilo' : 'Precio por Prenda'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {esPorKilo ? (
                <div className="flex items-center gap-2">
                  <Label className="w-32 text-sm shrink-0">Costo por kilo</Label>
                  <span className="text-sm text-gray-500">$</span>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    value={costoKilo}
                    onChange={(e) => setCostoKilo(e.target.value)}
                    className="w-36"
                  />
                </div>
              ) : isLoading ? (
                <div className="text-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto" />
                  <p className="text-sm text-gray-500 mt-2">Cargando prendas...</p>
                </div>
              ) : garments.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-3">
                  Este cliente no tiene prendas asignadas. Primero usa "Asignar Prendas".
                </p>
              ) : (
                <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
                  {garments.map((garment) => (
                    <div key={garment.prendaId} className="flex items-center gap-3 py-1.5 border-b last:border-0">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm truncate">{garment.nombre}</div>
                        <div className="text-xs text-gray-400">{garment.peso}g</div>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <span className="text-sm text-gray-500">$</span>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder="0.00"
                          value={garment.precio}
                          onChange={(e) => handlePriceChange(garment.prendaId, e.target.value)}
                          className="w-28 h-8 text-sm"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* === RECARGOS POR TIPO DE ENTREGA === */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Percent className="h-4 w-4 text-orange-500" />
                Recargos por Tipo de Entrega
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-3 py-2 px-3 bg-gray-50 rounded-md">
                <div className="flex-1">
                  <div className="text-sm font-medium">Ordinaria</div>
                  <div className="text-xs text-gray-500">Precio base sin recargo</div>
                </div>
                <Badge variant="secondary" className="text-xs font-semibold">100%</Badge>
              </div>

              <div className="flex items-center gap-3">
                <div className="flex-1">
                  <div className="text-sm font-medium">Extra</div>
                  <div className="text-xs text-gray-500">Recargo adicional sobre el precio base</div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <Input
                    type="number"
                    min="0"
                    max="200"
                    step="1"
                    placeholder="0"
                    value={porcentajeExtra}
                    onChange={(e) => setPorcentajeExtra(e.target.value)}
                    className="w-20 h-8 text-sm text-right"
                  />
                  <span className="text-sm text-gray-500">%</span>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="flex-1">
                  <div className="text-sm font-medium">Dominical</div>
                  <div className="text-xs text-gray-500">Recargo adicional sobre el precio base</div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <Input
                    type="number"
                    min="0"
                    max="200"
                    step="1"
                    placeholder="0"
                    value={porcentajeDominical}
                    onChange={(e) => setPorcentajeDominical(e.target.value)}
                    className="w-20 h-8 text-sm text-right"
                  />
                  <span className="text-sm text-gray-500">%</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={isSaving || isLoading}>
            {isSaving ? 'Guardando...' : 'Guardar Precios'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default GarmentPricesModal;
